import { FormikContextType, getIn } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IQuestion } from 'types/Question.types';
import {
  isAnswerEmpty,
  validateNumericField,
  validateOptionalNumericField,
} from 'utils/validation';
import { useAllowIncompleteAnswers } from 'utils/hooks/useTemplateConfigureParams';

export const useValidateTextAfterValueChange = (
  textValue: string,
  question: IQuestion,
  context: FormikContextType<any>
) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const allowIncompleteAnswers = useAllowIncompleteAnswers();
  useEffect(() => {
    let error;
    if (allowIncompleteAnswers && isAnswerEmpty(textValue)) {
      error = undefined;
    } else if (
      question.required ||
      question.min !== null ||
      question.max !== null
    ) {
      error = validateNumericField(
        (textValue || '')?.length.toString(),
        question.name,
        question.min,
        question.max,
        t
      );
    } else {
      error = validateOptionalNumericField(
        (textValue || '')?.length.toString(),
        question.name,
        question.min,
        question.max,
        t
      );
    }
    if (error) {
      setErrorMessage(error);
      context.setFieldError(question.initialKey, error);
    } else {
      setErrorMessage('');
      context.setFieldError(question.initialKey, undefined);
    }
  }, [
    textValue,
    getIn(context.values, question.initialKey),
    getIn(context.errors, question.initialKey),
    allowIncompleteAnswers,
  ]);

  useEffect(() => {
    if (errorMessage) {
      context.setFieldError(question.initialKey, errorMessage);
    } else {
      context.setFieldError(question.initialKey, undefined);
    }
  }, [errorMessage]);

  return errorMessage;
};

export const useValidateNumberAfterValueChange = (
  fieldValue: string | number,
  question: IQuestion,
  context: FormikContextType<any>
) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const allowIncompleteAnswers = useAllowIncompleteAnswers();

  useEffect(() => {
    let error;
    // A template may be saved with required questions left unanswered. An
    // answered number still goes through the min/max checks below.
    if (allowIncompleteAnswers && isAnswerEmpty(fieldValue)) {
      error = undefined;
    } else if (
      question.required ||
      ((question.min !== null || question.max !== null) && fieldValue)
    ) {
      error = validateNumericField(
        fieldValue?.toString(),
        question.name,
        question.min,
        question.max,
        t
      );
    } else {
      error = validateOptionalNumericField(
        fieldValue?.toString(),
        question.name,
        question.min,
        question.max,
        t
      );
    }
    if (error) {
      setErrorMessage(error);
      context.setFieldError(question.initialKey, error);
    } else {
      setErrorMessage('');
      context.setFieldError(question.initialKey, undefined);
    }
  }, [
    fieldValue,
    getIn(context.values, question.initialKey),
    getIn(context.errors, question.initialKey),
    question?.max,
    question?.min,
    allowIncompleteAnswers,
  ]);

  useEffect(() => {
    if (errorMessage) {
      context.setFieldError(question.initialKey, errorMessage);
    } else {
      context.setFieldError(question.initialKey, undefined);
    }
  }, [errorMessage]);

  return errorMessage;
};
